import React, {useCallback} from 'react';
import {ResizeCallbackData} from 'react-resizable';

import {
  getMaxSectionWidth,
  getSnappedColumn,
  getSnappedRowsPerPage,
} from '../../PanelBank/resizeUtils';
import {useWorkspaceSectionLayoutConfigs} from '../../PanelBank/WorkspaceSectionLayoutConfigs';
import {WorkspacePanelResizePreview} from './WorkspacePanelResizePreview';

export const useResizableWorkspacePanel = ({
  index,
  numVisiblePanels,
  setPanelResizePreview,
}: {
  index: number;
  numVisiblePanels: number;
  setPanelResizePreview: React.Dispatch<
    React.SetStateAction<WorkspacePanelResizePreview | null>
  >;
}) => {
  const {
    panelWidth,
    panelHeight,
    columnsPerPage,
    updateFlowConfig,
    numTotalPanelsInSection,
  } = useWorkspaceSectionLayoutConfigs();

  const handleOnResizeStart = useCallback(() => {
    setPanelResizePreview({
      index,
      height: panelHeight,
      width: panelWidth,
    });
  }, [setPanelResizePreview, index, panelHeight, panelWidth]);

  const handleOnResize = useCallback(
    (_: React.SyntheticEvent, data: ResizeCallbackData) => {
      setPanelResizePreview({
        index,
        height: data.size.height,
        width: data.size.width,
      });
    },
    [index, setPanelResizePreview]
  );

  const handleResizeStop = useCallback(
    (_: React.SyntheticEvent, data: ResizeCallbackData) => {
      const {columnCount: newColumnCount, width: newColumnWidth} =
        getSnappedColumn({
          currPanelWidth: data.size.width,
          maxWidth: getMaxSectionWidth({columnsPerPage, panelWidth}),
        });

      // Make sure to maintain the currently visible panels when resizing panel
      const newRowsPerPage = getSnappedRowsPerPage({
        numVisiblePanels,
        columnsPerPage: newColumnCount,
        numTotalPanelsInSection,
      });

      setPanelResizePreview(null);
      updateFlowConfig({
        columnsPerPage: newColumnCount,
        boxWidth: newColumnWidth,
        boxHeight: data.size.height,
        rowsPerPage: newRowsPerPage,
      });
    },
    [
      columnsPerPage,
      numTotalPanelsInSection,
      numVisiblePanels,
      panelWidth,
      setPanelResizePreview,
      updateFlowConfig,
    ]
  );
  return {
    handleOnResizeStart,
    handleOnResize,
    handleResizeStop,
  };
};
